import type { Feature, Position } from 'geojson'
import { cloneDeep } from 'lodash'

import customers from '@/customers/customers'

export function computeCustomArea(orgIds: string[]): string | undefined {
  const areas = customers
    .filter((c) => orgIds.includes(c.orgId))
    .filter((c) => !!c.area)
    .map((c) => c.area?.features ?? [])
  if (!areas.length) return undefined
  const fc = cloneDeep(featureCollectionTemplate)
  fc.features = areas.flat()
  return JSON.stringify(fc)
}

export interface EsriPolygon {
  rings: Position[][]
  spatialReference: { wkid: number }
}

// Joins the simplified areas of the given organizations into a single Esri JSON polygon
// (WGS84), the format feature services expect in the `geometry` query parameter.
export function computeFilterGeometry(orgIds: string[]): EsriPolygon | undefined {
  // Several tenants share the same area (e.g. South America); repeating its rings would
  // make them overlap, which Esri JSON can read as holes.
  const filterAreas = new Set(customers.filter((c) => orgIds.includes(c.orgId)).map((c) => c.filterArea))
  const rings = [...filterAreas]
    .flatMap((area) => area?.features ?? [])
    .flatMap(({ geometry }) => {
      if (geometry.type === 'Polygon') return geometry.coordinates
      if (geometry.type === 'MultiPolygon') return geometry.coordinates.flat()
      return []
    })
  if (!rings.length) return undefined
  return { rings, spatialReference: { wkid: 4326 } }
}

const featureCollectionTemplate = {
  type: 'FeatureCollection' as const,
  crs: { type: 'name', properties: { name: 'urn:ogc:def:crs:OGC:1.3:CRS84' } },
  features: [] as Feature[]
}
